import { SerializedError, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { REQUEST_METHODS } from '@lib/core/service/consts';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { parseError } from '@lib/core/service/requests/parseError';
import { request } from '@lib/core/service/requests/request';
import { locationLogApiUrlCreator } from '@lib/core/users/slices/urls';
import { ILocation } from '@lib/core/users/types';

export interface ILocationLogState {
  data: ILocation[];
  error: SerializedError;
  isLoading: boolean;
  isDataLoaded: boolean;
}

const initialState: ILocationLogState = {
  data: [],
  error: null,
  isDataLoaded: false,
  isLoading: false,
};

export const actionGetLocationLog = createTypedAsyncThunk('actionGetLocationLog', async () =>
  request({
    url: locationLogApiUrlCreator(),
  }),
);

export const actionUpdateLocationLog = createTypedAsyncThunk(
  'actionUpdateLocationLog',
  async (retailerLocationSlug: string, { getState }) => {
    try {
      const state = getState();
      const locationLog = state.users.locationLog.data;
      const isLocationInLog = locationLog.some(location => location?.retailerLocation?.slug === retailerLocationSlug);

      if (isLocationInLog) {
        return { location: null };
      }

      const response = await request({
        body: { retailerLocation: retailerLocationSlug },
        method: REQUEST_METHODS.POST,
        url: locationLogApiUrlCreator(),
      });

      return { location: response };
    } catch (error) {
      return Promise.reject(parseError(error));
    }
  },
);

const locationLogSlice = createSlice({
  extraReducers: builder => {
    builder.addMatcher(isAnyOf(actionGetLocationLog.pending, actionUpdateLocationLog.pending), state => {
      state.isDataLoaded = false;
      state.error = null;
      state.isLoading = true;
    });
    builder.addMatcher(isAnyOf(actionGetLocationLog.fulfilled), (state, action) => {
      state.data = action.payload.results;
      state.isDataLoaded = true;
      state.isLoading = false;
    });
    builder.addMatcher(isAnyOf(actionUpdateLocationLog.fulfilled), (state, action) => {
      if (action.payload.location) {
        state.data.push(action.payload.location);
      }
      state.isLoading = false;
    });

    builder.addMatcher(isAnyOf(actionGetLocationLog.rejected, actionUpdateLocationLog.rejected), (state, action) => {
      state.isDataLoaded = true;
      state.error = action.error;
      state.isLoading = false;
    });
  },
  initialState,
  name: 'locationLog',
  reducers: {
    actionResetLocationLog: () => initialState,
  },
});

export const { actionResetLocationLog } = locationLogSlice.actions;
export default locationLogSlice.reducer;
